// UsersInfoPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Container,
    FormHelperText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Layout from './Layout';
import UsersService from '../services/UsersService';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import styles from "./css/UsersInfoPage.module.css"

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^[+\d()\-.\s]{6,}$/; // simple & forgiving

export default function UsersInfoPage() {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { id } = useParams();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [saved, setSaved] = useState(false);
    const [touched, setTouched] = useState({});
    const [errors, setErrors] = useState({});

    const requiredFields = useMemo(
        () => ['username', 'role', 'firstName', 'lastName', 'phone', 'email'],
        []
    );

    const fetchUser = async () => {
        try {
            const data = await UsersService.getUserInfo(id);
            setUser(data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            showToast(t('usersInfo.toasts.errorLoadingUser'), 'error');
        }
    };

    useEffect(() => {
        fetchUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------- Validation ----------
    const validateField = (field, value) => {
        const v = String(value ?? '').trim();
        if (requiredFields.includes(field) && !v) return t('validation.required');
        if (field === 'email' && v && !emailRe.test(v)) return t('validation.email');
        if (field === 'phone' && v && !phoneRe.test(v)) return t('validation.phone');
        return '';
    };

    const validateAll = (candidate) => {
        const next = {};
        requiredFields.forEach((f) => {
            const msg = validateField(f, candidate?.[f]);
            if (msg) next[f] = msg;
        });
        return next;
    };

    const touchAll = () =>
        requiredFields.reduce((acc, f) => ({ ...acc, [f]: true }), {});

    const extractServerErrors = (err) => {
        // Expecting: { response: { data: { errors: { email: 'Already used', ... }}}}
        const server = err?.response?.data?.errors;
        if (!server || typeof server !== 'object') return null;
        const mapped = {};
        Object.entries(server).forEach(([field, message]) => {
            if (requiredFields.includes(field)) mapped[field] = String(message);
        });
        return mapped;
    };

    // ---------- Handlers ----------
    const handleChange = (field, value) => {
        setUser((prev) => ({ ...prev, [field]: value }));
        setSaved(false);

        if (touched[field]) {
            const msg = validateField(field, value);
            setErrors((prev) => {
                const next = { ...prev };
                if (msg) next[field] = msg;
                else delete next[field];
                return next;
            });
        }
    };

    const handleSave = async () => {
        const currentErrors = validateAll(user);
        setTouched((tchs) => ({ ...tchs, ...touchAll() }));
        setErrors(currentErrors);

        if (Object.keys(currentErrors).length > 0) {
            showToast(t('usersInfo.toasts.fixValidation'), 'error');
            return;
        }

        try {
            await UsersService.updateUser(id, user);
            setSaved(true);
            showToast(t('usersInfo.toasts.saved'), 'success');
        } catch (e) {
            console.error('Failed to save user:', e);
            const serverErrors = extractServerErrors(e);
            if (serverErrors && Object.keys(serverErrors).length > 0) {
                setTouched((tchs) => ({ ...tchs, ...touchAll() }));
                setErrors((prev) => ({ ...prev, ...serverErrors }));
                showToast(t('usersInfo.toasts.reviewHighlighted'), 'error');
            } else {
                showToast(t('usersInfo.toasts.errorSaving'), 'error');
            }
        }
    };

    const handleDelete = async () => {
        const confirmed = window.confirm(
            t('usersInfo.confirm.deleteUserQuestion')
        );
        if (!confirmed) return;
        try {
            await UsersService.deleteUser(id);
            showToast(t('usersInfo.toasts.deleted'), 'success');
            navigate('/users');
        } catch (error) {
            console.error('Failed to delete user:', error);
            showToast(t('usersInfo.toasts.errorDeleting'), 'error');
        }
    };

    return (
        <Layout>
            {/* (Optional) consider Container maxWidth="xl" instead of "xd" */}
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Box className={styles.pagePad}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        className={styles.backBtn}
                    >
                        {t("common.back")}
                    </Button>

                    <Paper className={styles.card} elevation={3}>
                        <Typography variant="h5" gutterBottom>
                            {t("usersInfo.title")}
                        </Typography>

                        {/* Row 1 */}
                        <Box className={styles.grid2}>
                            <TextField
                                label={t("usersInfo.form.username")}
                                fullWidth
                                required
                                value={user?.username || ""}
                                onChange={(e) => handleChange("username", e.target.value)}
                                error={touched.username && Boolean(errors.username)}
                                helperText={touched.username && errors.username}
                                disabled
                            />

                            <FormControl
                                fullWidth
                                required
                                error={touched.role && Boolean(errors.role)}
                            >
                                <InputLabel>{t("usersInfo.form.role")}</InputLabel>
                                <Select
                                    value={user?.role || ""}
                                    label={t("usersInfo.form.role")}
                                    onChange={(e) => handleChange("role", e.target.value)}
                                >
                                    <MenuItem value="ADMIN">ADMIN</MenuItem>
                                    <MenuItem value="USER">USER</MenuItem>
                                </Select>
                                {touched.role && errors.role && (
                                    <FormHelperText>{errors.role}</FormHelperText>
                                )}
                            </FormControl>
                        </Box>

                        {/* Row 2 */}
                        <Box className={styles.grid2}>
                            <TextField
                                label={t("usersInfo.form.firstName")}
                                fullWidth
                                required
                                value={user?.firstName || ""}
                                onChange={(e) => handleChange("firstName", e.target.value)}
                                error={touched.firstName && Boolean(errors.firstName)}
                                helperText={touched.firstName && errors.firstName}
                            />
                            <TextField
                                label={t("usersInfo.form.lastName")}
                                fullWidth
                                required
                                value={user?.lastName || ""}
                                onChange={(e) => handleChange("lastName", e.target.value)}
                                error={touched.lastName && Boolean(errors.lastName)}
                                helperText={touched.lastName && errors.lastName}
                            />
                        </Box>

                        {/* Row 3 */}
                        <Box className={styles.grid2}>
                            <TextField
                                label={t("usersInfo.form.phone")}
                                fullWidth
                                required
                                value={user?.phone || ""}
                                onChange={(e) => handleChange("phone", e.target.value)}
                                error={touched.phone && Boolean(errors.phone)}
                                helperText={touched.phone && errors.phone}
                            />
                            <TextField
                                label={t("usersInfo.form.email")}
                                type="email"
                                fullWidth
                                required
                                value={user?.email || ""}
                                onChange={(e) => handleChange("email", e.target.value)}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                            />
                        </Box>

                        {/* Actions */}
                        <Box className={styles.actionsRow}>
                            <Button variant="outlined" color="error" onClick={handleDelete}>
                                {t("usersInfo.buttons.deleteUser")}
                            </Button>

                            <Button
                                variant="contained"
                                onClick={handleSave}
                                className={styles.primaryBtn}
                            >
                                {t("usersInfo.buttons.saveChanges")}
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </Box>
        </Layout>
    );
}
